import useStore from 'Hooks/useStore';
import { appConfigStore } from 'Models/AppConfigStore';
import * as React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Button } from 'Views/Components/Button/Button';
import { BookingDetailSection, SectionProps } from './BookingDetailSection';
import { BookingSummary } from '../../../BookingSummaryDto';
import { useLocationNamesFromBookingSummary } from '../Hooks/useLocationNamesFromBookingSummary';
import { useJourneySchedule } from 'Global/Journey/Queries/useJourneySchedule';
import { useStopsBetween } from 'Modules/MultiStopWizard/Hooks/useStopsBetween';
import Skeleton from '../../../Skeleton/Skeleton';
import If from '../../../../If/If';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';

export function FerryTripDetailsSection({ booking, canEdit, onEdit, isInvoiced }: SectionProps) {
	const intl = useIntl();
	if (!booking.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return <></>;
	}

	const sectionContent = <TripDateTime bookingSummary={booking.bookedSummary} />;

	return (
		<BookingDetailSection
			sectionName={intl.formatMessage({
				id: 'booking-details__ferry-trip-details-section__label',
				defaultMessage: 'Ferry trip details',
			})}
			sectionContent={sectionContent}
			sectionClassName="trip-time"
			icon={{
				name: 'calendar_today',
				useMaterialIcons: true,
			}}
			modificationRules={{
				canEdit: canEdit,
				editType: 'trip-time',
				onEdit: onEdit,
				isInvoiced: isInvoiced,
			}}
		/>
	);
}

interface TripDateTimeProps {
	bookingSummary: BookingSummary;
}

function TripDateTime({ bookingSummary }: TripDateTimeProps) {
	const { startLocationName, endLocationName } = useLocationNamesFromBookingSummary(bookingSummary);
	const store = useStore();

	const ferryTrip = bookingSummary?.ferryTrip;

	const schedule = useJourneySchedule({
		tripId: ferryTrip?.id,
		startStopId: bookingSummary.startStopId,
		endStopId: bookingSummary.endStopId,
	});

	const { count: numberOfStopsBetween } = useStopsBetween(bookingSummary.startStopId, bookingSummary.endStopId);

	return (
		<>
			<div className="trip-details__table">
				<div className="row">
					<div className="cell">
						<div className="cell__header">
							<p>
								<FormattedMessage
									id="booking-details__ferry-trip-details-section__departure-field__label"
									defaultMessage="Departure"
								/>
							</p>
							<If condition={store.isStaff && isNotNullOrUndefined(ferryTrip?.id)}>
								<Link
									to={`/ferry-schedule/edit/${ferryTrip?.id}`}
									className="view-ferry-trip__button hide-underline"
								>
									<Icon name="info" useMaterialIcons />
								</Link>
							</If>
						</div>
						<p>{startLocationName}</p>
						<p>
							{!schedule.data && <Skeleton width="4rem" />}
							{schedule.data && (
								<>{`${dayjs(schedule.data.departureDateTime).format('DD MMM YYYY')}, ${formatStandardTime(schedule.data.departureDateTime)}`}</>
							)}
						</p>
					</div>
					<div className="cell">
						<div className="cell__header">
							<p>
								<FormattedMessage
									id="booking-details__ferry-trip-details-section__arrival-field__label"
									defaultMessage="Arrival"
								/>
							</p>
						</div>
						<p>{endLocationName}</p>
						<p>
							{!schedule.data && <Skeleton width="4rem" />}
							{schedule.data && (
								<>{`${dayjs(schedule.data.arrivalDateTime).format('DD MMM YYYY')}, ${formatStandardTime(schedule.data.arrivalDateTime)}`}</>
							)}
						</p>
					</div>
					<If condition={stringNotEmpty(ferryTrip?.name) && appConfigStore.showVoyageNumberInput}>
						<div className="cell">
							<div className="cell__header">
								<p>
									<FormattedMessage
										id="booking-details__ferry-trip-details-section__ferry-name-field__label"
										defaultMessage="Ferry no."
									/>
								</p>
							</div>
							<p>{ferryTrip.name?.toUpperCase()}</p>
						</div>
					</If>
					<If condition={numberOfStopsBetween > 0}>
						<div className="cell">
							<div className="cell__header">
								<p>
									<FormattedMessage
										id="booking-details__ferry-trip-details-section__number-of-stops-field__label"
										defaultMessage="No. of stops"
									/>
								</p>
							</div>
							<p>{numberOfStopsBetween}</p>
						</div>
					</If>
				</div>
			</div>
		</>
	);
}
